import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { preventInputFormSubmitOnEnter } from "@/features/post/components/forms/submitShortcut";
import {
  createThreadNgWordEditorMediator,
  isThreadNgWordEditorEvent,
  type ThreadNgWordEditorEffect,
  type ThreadNgWordEditorEvent,
  type ThreadNgWordEditorState,
} from "@/features/thread/mediators/threadNgWordEditorMediator";
import {
  readThreadNgPresets,
  THREAD_NG_PRESETS_KEY,
  THREAD_NG_PRESETS_UPDATED_EVENT,
  writeThreadNgPresets,
} from "@/features/thread/stores/threadNgPresetStorage";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
import { Input, Textarea } from "@/shared/ui/form";

interface Props {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export function ThreadNgWordEditor({
  value,
  onChange,
  disabled = false,
}: Props): React.ReactNode {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [mediator] = useState(() =>
    createThreadNgWordEditorMediator(
      (effect: ThreadNgWordEditorEffect) => {
        if (effect.type === "change-value") {
          onChangeRef.current(effect.value);
          return;
        }
        const success = writeThreadNgPresets(effect.presets);
        mediator.dispatch({
          type: "thread-ng-editor/write-completed",
          kind: effect.kind,
          name: effect.name,
          presets: effect.presets,
          success,
        });
      },
      { value, disabled, presets: readThreadNgPresets() },
    ),
  );
  useHierarchyLifetime(mediator);
  const node = useComponentNode((event) => {
    if (!isThreadNgWordEditorEvent(event)) return "unhandled";
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  const notify = useCallback(
    (event: ThreadNgWordEditorEvent): void => {
      node.dispatch(event);
    },
    [node],
  );
  const state = useMediatorSnapshot(mediator);
  useLayoutEffect(() => {
    notify({ type: "thread-ng-editor/model-synchronized", value, disabled });
  }, [disabled, notify, value]);
  const refreshPresets = useCallback((): void => {
    notify({
      type: "thread-ng-editor/presets-synchronized",
      presets: readThreadNgPresets(),
    });
  }, [notify]);
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key === THREAD_NG_PRESETS_KEY) refreshPresets();
    };
    window.addEventListener(THREAD_NG_PRESETS_UPDATED_EVENT, refreshPresets);
    window.addEventListener("storage", onStorage);
    return (): void => {
      window.removeEventListener(
        THREAD_NG_PRESETS_UPDATED_EVENT,
        refreshPresets,
      );
      window.removeEventListener("storage", onStorage);
    };
  }, [refreshPresets]);
  return (
    <ComponentScope node={node}>
      <ThreadNgWordEditorView
        state={state}
        onEvent={notify}
        onRefresh={refreshPresets}
      />
    </ComponentScope>
  );
}

export function ThreadNgWordEditorView({
  state: {
    value,
    validation,
    disabled,
    presets,
    name,
    selectedName,
    storageMessage,
  },
  onEvent,
  onRefresh,
}: {
  state: ThreadNgWordEditorState;
  onEvent: (event: ThreadNgWordEditorEvent) => void;
  onRefresh: () => void;
}): React.ReactNode {
  const id = useId();
  const wordsId = `${id}-thread-ng-words`;
  const helpId = `${id}-thread-ng-help`;
  const nameId = `${id}-thread-ng-preset-name`;
  const selectId = `${id}-thread-ng-preset-select`;

  return (
    <section className="min-w-0 border-t border-border pt-3">
      <h3 className="text-sm font-medium text-foreground">
        スレッドNGワード（正規表現）
      </h3>
      <div className="mt-3 space-y-3">
        <p className="text-xs text-muted-foreground">
          1行に1つの正規表現を入力します。大文字と小文字は区別します。最大20件、各500文字。作成後は変更できません。
        </p>
        <label htmlFor={wordsId} className="block text-sm text-foreground">
          NGワードの正規表現
        </label>
        <Textarea
          id={wordsId}
          value={value}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void =>
            onEvent({
              type: "thread-ng-editor/value-changed",
              value: event.target.value,
            })
          }
          onKeyDown={(event: React.KeyboardEvent<HTMLTextAreaElement>): void =>
            event.stopPropagation()
          }
          rows={4}
          placeholder={"spam|広告\n^定型文$"}
          aria-describedby={helpId}
          aria-invalid={validation.error !== null}
          error={validation.error !== null}
          disabled={disabled}
        />
        <p id={helpId} className="text-xs text-muted-foreground">
          スラッシュやフラグは不要です。レス本文への一致をサーバーが判定します。
        </p>
        {validation.error && (
          <p role="alert" className="text-xs text-destructive">
            {validation.error}
          </p>
        )}
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
          <label htmlFor={nameId} className="sr-only">
            プリセット名
          </label>
          <Input
            id={nameId}
            value={name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
              onEvent({
                type: "thread-ng-editor/name-changed",
                name: event.target.value,
              })
            }
            onKeyDown={preventInputFormSubmitOnEnter}
            placeholder="プリセット名"
            disabled={disabled}
          />
          <button
            type="button"
            className="rounded-lg border border-border px-3 py-2 text-sm text-foreground disabled:opacity-50"
            onClick={(): void =>
              onEvent({ type: "thread-ng-editor/save-requested" })
            }
            disabled={disabled || !name.trim() || !validation.patterns?.length}
          >
            保存
          </button>
        </div>
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto_auto]">
          <label htmlFor={selectId} className="sr-only">
            保存済みプリセット
          </label>
          <select
            id={selectId}
            value={selectedName}
            onFocus={onRefresh}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>): void =>
              onEvent({
                type: "thread-ng-editor/selection-changed",
                name: event.target.value,
              })
            }
            className="min-w-0 rounded-lg border border-border bg-muted px-3 py-2 text-sm text-foreground"
            disabled={disabled}
          >
            <option value="">保存済みプリセットを選択</option>
            {presets.map((preset) => (
              <option key={preset.name} value={preset.name}>
                {preset.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="rounded-lg border border-border px-3 py-2 text-sm text-foreground disabled:opacity-50"
            onClick={(): void =>
              onEvent({ type: "thread-ng-editor/apply-requested" })
            }
            disabled={disabled || !selectedName}
          >
            呼び出す
          </button>
          <button
            type="button"
            className="rounded-lg border border-border px-3 py-2 text-sm text-destructive disabled:opacity-50"
            onClick={(): void =>
              onEvent({ type: "thread-ng-editor/delete-requested" })
            }
            disabled={disabled || !selectedName}
          >
            削除
          </button>
        </div>
        {storageMessage && (
          <p role="status" className="text-xs text-foreground">
            {storageMessage}
          </p>
        )}
      </div>
    </section>
  );
}
